import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from 'src/app/shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from 'src/app/shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from 'src/app/shared/page-layout/page-metric-strip.component';
import { PageShellComponent } from 'src/app/shared/page-layout/page-shell.component';
import { PageMetric } from 'src/app/shared/page-layout/page-layout.models';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { BankReconciliationService } from '../../bank-reconciliation/bank-reconciliation.service';
import { BankAccountDashboardRow } from '../../bank-reconciliation/models/bank-reconciliation.model';
import { BankAccount } from '../../bank-account/bank-account.model';
import { BankAccountService } from '../../bank-account/bank-account.service';

@Component({
    selector: 'app-banking-bank-account-view',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ButtonModule,
        TagModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent
    ],
    template: `
        <app-page-shell>
            <app-page-detail-hero
                icon="pi pi-wallet"
                eyebrow="Bank account"
                [title]="account?.accountName || 'Bank account'"
                [subtitle]="accountSubtitle">
                <div tags>
                    <p-tag [value]="account?.currencyCode || '-'" severity="info"></p-tag>
                    <p-tag [value]="account?.reconciliationEnabled ? 'Reconciliation enabled' : 'Reconciliation disabled'" [severity]="account?.reconciliationEnabled ? 'success' : 'secondary'"></p-tag>
                    <p-tag [value]="account?.lastReconciledDate ? 'Last reconciled ' + account?.lastReconciledDate : 'Never reconciled'" severity="warn"></p-tag>
                </div>
                <div actions>
                    <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/accounts/enterprise/banking/bank-accounts"></p-button>
                    <p-button label="Edit" icon="pi pi-pencil" [routerLink]="['/accounts/enterprise/banking/bank-accounts', account?.id, 'edit']" [disabled]="!account?.id"></p-button>
                </div>
            </app-page-detail-hero>

            <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

            <div class="detail-grid">
                <app-page-data-panel title="Account identity" subtitle="Bank and owner details">
                    <dl class="facts">
                        <div><dt>Account name</dt><dd>{{ account?.accountName || '-' }}</dd></div>
                        <div><dt>Account number</dt><dd>{{ account?.accountNumber || '-' }}</dd></div>
                        <div><dt>IBAN</dt><dd>{{ account?.iban || '-' }}</dd></div>
                        <div><dt>Currency</dt><dd>{{ account?.currencyCode || '-' }}</dd></div>
                        <div><dt>Owner</dt><dd>{{ ownerLabel }}</dd></div>
                        <div><dt>Owner type</dt><dd>{{ account?.party?.id?.entityType || '-' }}</dd></div>
                    </dl>
                </app-page-data-panel>

                <app-page-data-panel title="Bank institution" subtitle="Read-only bank master information">
                    <div class="bank-card">
                        @if (account?.bank?.logoUrl) {
                            <img [src]="account?.bank?.logoUrl" alt="">
                        }
                        <div>
                            <strong>{{ account?.bank?.name || 'Unassigned bank' }}</strong>
                            <span>{{ account?.bank?.legalName || '-' }}</span>
                            <small>SWIFT {{ account?.bank?.swiftCode || '-' }}</small>
                        </div>
                    </div>
                </app-page-data-panel>

                <app-page-data-panel title="Accounting linkage" subtitle="GL and adjustment accounts">
                    <dl class="facts">
                        <div><dt>GL bank account</dt><dd>{{ account?.account?.accountName || 'Missing' }}</dd></div>
                        <div><dt>GL account code</dt><dd>{{ account?.account?.accountCode || '-' }}</dd></div>
                        <div><dt>Default suspense account</dt><dd>{{ account?.defaultSuspenseAccountId || '-' }}</dd></div>
                        <div><dt>Bank fees account</dt><dd>{{ account?.bankFeesAccountId || '-' }}</dd></div>
                        <div><dt>Interest income account</dt><dd>{{ account?.interestIncomeAccountId || '-' }}</dd></div>
                        <div><dt>Interest expense account</dt><dd>{{ account?.interestExpenseAccountId || '-' }}</dd></div>
                    </dl>
                </app-page-data-panel>

                <app-page-data-panel title="Reconciliation settings" subtitle="Operational close and matching setup">
                    <dl class="facts">
                        <div><dt>Enabled</dt><dd>{{ account?.reconciliationEnabled ? 'Yes' : 'No' }}</dd></div>
                        <div><dt>Variance tolerance</dt><dd>{{ account?.reconciliationVarianceTolerance ?? '-' }}</dd></div>
                        <div><dt>Last reconciled date</dt><dd>{{ account?.lastReconciledDate || 'Never' }}</dd></div>
                    </dl>
                </app-page-data-panel>
            </div>

            <app-page-data-panel title="Quick actions" subtitle="Open reconciliation workspaces already filtered to this bank account.">
                <div class="quick-actions">
                    <p-button label="Import statement" icon="pi pi-upload" [routerLink]="['/accounts/enterprise/bank-reconciliation/statements/import']" [queryParams]="bankAccountQuery"></p-button>
                    <p-button label="Statements" icon="pi pi-building-columns" severity="secondary" [outlined]="true" [routerLink]="['/accounts/enterprise/bank-reconciliation/statements']" [queryParams]="bankAccountQuery"></p-button>
                    <p-button label="Dashboard" icon="pi pi-chart-line" severity="secondary" [outlined]="true" [routerLink]="['/accounts/enterprise/bank-reconciliation/dashboard']" [queryParams]="bankAccountQuery"></p-button>
                    <p-button label="Unmatched lines" icon="pi pi-list-check" severity="secondary" [outlined]="true" [routerLink]="['/accounts/enterprise/bank-reconciliation/reports/unmatched-bank-lines']" [queryParams]="bankAccountQuery"></p-button>
                    <p-button label="Rules" icon="pi pi-wrench" severity="secondary" [outlined]="true" [routerLink]="['/accounts/enterprise/bank-reconciliation/rules']" [queryParams]="bankAccountQuery"></p-button>
                </div>
            </app-page-data-panel>
        </app-page-shell>
    `,
    styles: [`
        .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
            gap: 1rem;
        }

        .facts {
            display: grid;
            gap: 0.75rem;
            margin: 0;
        }

        .facts div {
            display: grid;
            grid-template-columns: minmax(140px, 0.45fr) 1fr;
            gap: 0.75rem;
            padding-bottom: 0.75rem;
            border-bottom: 1px solid #eef2f7;
        }

        .facts dt {
            color: #64748b;
            font-weight: 700;
        }

        .facts dd {
            color: #172033;
            margin: 0;
            word-break: break-word;
        }

        .bank-card,
        .quick-actions {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 0.75rem;
        }

        .bank-card img {
            width: 54px;
            height: 54px;
            border-radius: 50%;
            object-fit: cover;
            background: #f1f5f9;
        }

        .bank-card div {
            display: flex;
            flex-direction: column;
            gap: 0.2rem;
        }

        .bank-card span,
        .bank-card small {
            color: #64748b;
        }
    `]
})
export class BankAccountViewComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);
    private readonly bankAccountService = inject(BankAccountService);
    private readonly bankReconciliationService = inject(BankReconciliationService);
    private readonly sessionStorageService = inject(SessionStorageService);

    account?: BankAccount;
    dashboardRow?: BankAccountDashboardRow;

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        if (!id) return;
        this.bankAccountService.getBankAccountById(id).subscribe(account => {
            this.account = account;
            this.loadOperationalCards(account.id);
        });
    }

    get accountSubtitle(): string {
        if (!this.account) return '';
        return `${this.account.bank?.name || 'Unassigned bank'} · ${this.account.accountNumber || this.account.iban || 'No account number'}`;
    }

    get ownerLabel(): string {
        return this.account?.party?.entityName || `${this.account?.party?.id?.entityType || 'Owner'} #${this.account?.party?.id?.entityId || '-'}`;
    }

    get bankAccountQuery(): Record<string, number> {
        return this.account?.id ? { bankAccountId: this.account.id } : {};
    }

    get metrics(): PageMetric[] {
        const row = this.dashboardRow;
        return [
            { label: 'Open statements', value: row?.openStatementCount ?? 0, tone: 'info' },
            { label: 'Open sessions', value: row?.openSessionCount ?? 0, tone: 'info' },
            { label: 'Unmatched bank lines', value: row?.unmatchedLineCount ?? 0, hint: row?.unmatchedAmount ?? 0, tone: row?.unmatchedLineCount ? 'warn' : 'default' },
            { label: 'Open exceptions', value: row?.openExceptionCount ?? 0, tone: row?.openExceptionCount ? 'danger' : 'default' },
            { label: 'Variance total', value: row?.varianceTotal ?? 0, tone: row?.varianceTotal ? 'warn' : 'default' }
        ];
    }

    private loadOperationalCards(bankAccountId: number): void {
        const companyId = this.sessionStorageService.getCompany()?.id ?? 0;
        if (!companyId) return;
        this.bankReconciliationService.getDashboard(companyId, bankAccountId).subscribe({
            next: dashboard => this.dashboardRow = dashboard.bankAccounts?.find(row => row.bankAccountId === bankAccountId),
            error: () => this.dashboardRow = undefined
        });
    }
}
